import { useEffect, useState } from "react";
import { portalApi } from "./PortalShell";
import { Calendar, TrendingUp, Trophy, Zap, Copy, Check, Send } from "lucide-react";
import { toast } from "sonner";

const fmtRange = (start, end) => {
  try {
    const s = new Date(start);
    const e = new Date(end);
    const sameMonth = s.getMonth() === e.getMonth();
    const opts = { day: "numeric", month: sameMonth ? undefined : "short" };
    return `${s.toLocaleDateString("en-IN", opts)} – ${e.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`;
  } catch {
    return "";
  }
};

export default function WeeklyDigestSection() {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    portalApi
      .get("/affiliate/weekly-digest")
      .then((r) => setDigest(r.data.digest))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!digest) return null; // Nothing to preview yet — the cron hasn't fired for this affiliate.

  const wins = digest.last_week_wins || {};
  const target = digest.this_week_target;
  const action = digest.top_action;

  const copyBody = async () => {
    try {
      await navigator.clipboard.writeText(digest.whatsapp_body || "");
      setCopied(true);
      toast.success("Copied — paste into WhatsApp");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };

  return (
    <div
      data-testid="weekly-digest-section"
      className="rounded-3xl bg-neutral-900 border border-neutral-800 overflow-hidden"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-neutral-800 bg-gradient-to-r from-emerald-950/60 via-neutral-900 to-teal-950/40 flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0">
          <Calendar className="h-5 w-5 text-white" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 flex items-center gap-1">
            <Send className="h-3 w-3" /> Monday · WhatsApp digest
          </div>
          <div className="text-lg font-bold tracking-tight">This Week's Playbook</div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 text-right shrink-0">
          {fmtRange(digest.week_start, digest.week_end)}
        </div>
      </div>

      {/* Body */}
      <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Last week's wins */}
        <div className="rounded-2xl bg-neutral-950 border border-neutral-800 p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 flex items-center gap-1">
            <Trophy className="h-3 w-3" /> Last week's wins
          </div>
          {wins.sales > 0 || wins.new_customers > 0 ? (
            <>
              <div className="text-2xl font-bold tabular-nums leading-none">
                ₹{Math.round(wins.sales || 0).toLocaleString("en-IN")}
              </div>
              <div className="text-[11px] text-neutral-400 mt-1">
                {wins.new_customers} new customer{wins.new_customers === 1 ? "" : "s"} · {wins.order_count} order{wins.order_count === 1 ? "" : "s"}
              </div>
              {wins.tier_movement && (
                <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">
                  🏆 {wins.tier_movement.tier}
                </div>
              )}
              {wins.referral_rewards > 0 && (
                <div className="mt-2 text-[11px] text-emerald-400">
                  🎁 {wins.referral_rewards} referral reward{wins.referral_rewards === 1 ? "" : "s"} unlocked
                </div>
              )}
            </>
          ) : (
            <div className="text-[12px] text-neutral-500 leading-relaxed">
              A quiet week — new one starts today. Let's move.
            </div>
          )}
        </div>

        {/* This week's target */}
        <div className="rounded-2xl bg-orange-500/5 border border-orange-500/30 p-4">
          <div className="text-[10px] uppercase tracking-widest text-orange-300 mb-2 flex items-center gap-1">
            <TrendingUp className="h-3 w-3" /> This week's target
          </div>
          {target ? (
            <>
              <div className="text-2xl font-bold tabular-nums leading-none">
                +{target.customers_needed}
              </div>
              <div className="text-[11px] text-neutral-300 mt-1 leading-relaxed">
                new customer{target.customers_needed === 1 ? "" : "s"} → commission percent jumps to <b className="text-orange-300">{target.target_pct}%</b>
              </div>
              <div className="text-[10px] text-neutral-500 mt-1">
                Currently at {target.current_pct}%
              </div>
            </>
          ) : (
            <div className="text-[12px] text-neutral-500 leading-relaxed">
              You're at the top slab — keep the streak alive.
            </div>
          )}
        </div>

        {/* Top action from the Copilot */}
        <div className="rounded-2xl bg-neutral-950 border border-neutral-800 p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 flex items-center gap-1">
            <Zap className="h-3 w-3" /> Top action
          </div>
          {action ? (
            <>
              <div className="text-[13px] font-semibold text-white leading-snug">
                {action.title}
              </div>
              {action.time_estimate && (
                <div className="text-[10px] text-neutral-500 mt-1.5">⏱ {action.time_estimate}</div>
              )}
              {action.impact && (
                <div className={`mt-2 inline-block text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${action.impact === "high" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>
                  {action.impact}
                </div>
              )}
            </>
          ) : (
            <div className="text-[12px] text-neutral-400 leading-relaxed">
              Open <b className="text-neutral-200">How To Increase Your Earnings</b> → answer 4 quick questions for a tailored plan.
            </div>
          )}
        </div>
      </div>

      {/* Footer — WhatsApp preview + copy */}
      {digest.whatsapp_body && (
        <div className="px-5 pb-5">
          <div className="rounded-2xl bg-neutral-950 border border-neutral-800 p-3">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
                <Send className="h-3 w-3" /> WhatsApp message we'll send
              </div>
              <button
                onClick={copyBody}
                data-testid="weekly-digest-copy"
                className="inline-flex items-center gap-1 text-[11px] text-emerald-300 hover:text-emerald-200"
              >
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} Copy
              </button>
            </div>
            <pre className="text-[11px] text-neutral-300 whitespace-pre-wrap font-sans leading-relaxed">
              {digest.whatsapp_body}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
